import { Drawer } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useMatches } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { UnlockCards } from "../achievements/UnlockCard";
import { InvitationsBanner } from "../InvitationsForYou";
import { readPref, writePref } from "../prefs";
import { Header, type ShellTeam } from "./Header";
import classes from "./shell.module.css";
import { Sidebar } from "./Sidebar";

/**
 * Every signed-in page's frame (design-system spec 2): the 60px header, then the sidebar and the
 * page, which scrolls on its own. A team's pages, and the staff pages in a team's frame, pass
 * `team` and get the sidebar; the others (a new team, no team yet) get the header alone. Below
 * `sm` the sidebar is a drawer.
 */
export function Shell({ team, children }: { team?: ShellTeam; children: ReactNode }) {
  const [open, setOpen] = useState(() => readPref("sidebarOpen"));
  const [drawer, setDrawer] = useState(false);
  const phone = useMediaQuery("(max-width: 48em)") === true;
  // A full-window page (staticData.fullWindow) covers the frame: what it covers is inert.
  const covered = useMatches({
    select: (matches) => matches.some((match) => match.staticData.fullWindow === true),
  });
  const toggle = () => {
    if (phone) {
      setDrawer(!drawer);
      return;
    }
    writePref("sidebarOpen", !open);
    setOpen(!open);
  };
  return (
    <div className={classes.shell}>
      <Header
        inert={covered}
        team={team}
        sidebarOpen={phone ? drawer : open}
        onToggleSidebar={team === undefined ? undefined : toggle}
      />
      {/* Beside the header, never in it: a full-window page makes the header inert, and an unlock's
          announcement is heard over every page (spec web/achievements, 4). */}
      {team === undefined ? null : <UnlockCards teamId={team.teamId} />}
      <div className={classes.body}>
        {team === undefined || phone ? null : (
          <aside className={classes.sidebar} data-open={open || undefined} inert={covered}>
            <Sidebar teamId={team.teamId} role={team.role} open={open} />
          </aside>
        )}
        {/* The router restores and resets this element's scroll (app/router.tsx). */}
        <main id="main" className={classes.main} data-scroll-restoration-id="main">
          {/* A full-window page covers the frame: the invitations would sit, focusable, under it. */}
          {covered ? null : <InvitationsBanner />}
          {children}
        </main>
      </div>
      {team === undefined || !phone ? null : (
        <Drawer.Root opened={drawer} onClose={() => setDrawer(false)} size={240}>
          <Drawer.Overlay backgroundOpacity={0.5} blur={2} />
          {/* The compound parts, so the dialog has a name without a visible title. */}
          <Drawer.Content aria-label="Navigation">
            <Drawer.Body className={classes.drawerBody}>
              <Sidebar
                teamId={team.teamId}
                role={team.role}
                open
                onNavigate={() => setDrawer(false)}
              />
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </div>
  );
}
